import React, { useMemo, useState } from 'react';
import { HashRouter } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import AppRoutes from './routes/AppRoutes';
import useApprovals from './hooks/useApprovals';
import useKeyboard from './hooks/useKeyboard';
import ToastProvider from '@/components/feedback/Toast';

function KeyboardController() {
  const { approvals, selectedApprovalId, actOnApproval } = useApprovals();
  const [toast, setToast] = useState('');
  const selectedApproval = approvals.find((approval) => approval.id === selectedApprovalId);

  const act = (action) => {
    if (!selectedApproval) return;
    actOnApproval(selectedApproval.id, action);
    setToast(`${selectedApproval.title} ${action}`);
    window.setTimeout(() => setToast(''), 2400);
  };

  const bindings = useMemo(() => ({
    a: () => act('approved'),
    r: () => act('rejected'),
    m: () => act('modified')
  }), [selectedApproval, actOnApproval]);

  useKeyboard(bindings, window.location.hash.includes('approvals'));

  return (
    <AnimatePresence>
      {toast && (
        <motion.div className="toast" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
          {toast}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function App() {
  // Cutover §3 honesty: the simulated WS heartbeat (realtime/ws/
  // createHeartbeat) is GONE. It fed the realtime slice a synthetic
  // 'connected' state + fresh heartbeats, so ConnectionIndicator showed
  // "Live" and ReconnectBanner's machinery ran against a heartbeat no real
  // transport produced. The slice now boots at 'no-transport' (see
  // store/slices/realtimeSlice.js): the indicator reads "No live feed"
  // (honest), and the banner stays silent until a real stream exists to
  // disconnect. When the real WS/SSE transport lands (B7), App mounts it
  // exactly here and setConnectionStatus('connected'|'connecting'|...) is
  // driven by the transport — the consumers need no further changes.
  return (
    <ToastProvider>
      <HashRouter>
        <AppRoutes />
        <KeyboardController />
      </HashRouter>
    </ToastProvider>
  );
}

export default App;
